'use client';

import { type ReactNode } from 'react';
import { useAuth } from '@clerk/nextjs';
import { ReachCopilotTopbarButton } from '@/components/copilot/reach-copilot-topbar-button';
import { MobileLanguageQuickButton } from '@/components/layout/mobile-language-quick-button';
import type { MobileSidePanelTheme } from '@/components/layout/mobile-app-side-panel-theme';
import { isSuperAdminViewClient } from '@/lib/permissions/client';
import { cn } from '@/lib/utils';
import { useRTLUtils } from '@/lib/translation';

/** ~10% shorter than min-h-14 (56px) — shared by SaaS navbar and custom shell mobile headers. */
export const MOBILE_SHELL_TOP_BAR_HEIGHT_CLASS = 'min-h-[50px]';

export const mobileShellTopBarClassName = cn(
  'print:hidden relative z-30 flex shrink-0 items-center gap-2 border-b border-emerald-200/80 bg-white/90 ps-1 pe-1 backdrop-blur-sm',
  'dark:border-emerald-900/50 dark:bg-emerald-950/90 md:hidden',
  MOBILE_SHELL_TOP_BAR_HEIGHT_CLASS,
  'pt-[env(safe-area-inset-top,0px)]',
);

export function mobileShellTopBarTitleClassName(textAlignClass?: string) {
  return cn(
    'min-w-0 flex-1 truncate text-sm font-semibold text-emerald-950 dark:text-emerald-50',
    textAlignClass,
  );
}

/** Standard mobile shell trigger — burger on the start edge (right in RTL, left in LTR). */
export const mobileShellTopBarMenuTriggerClassName = cn(
  'relative z-10 -ms-1.5 h-11 w-11 shrink-0 touch-manipulation rounded-md [&_svg]:size-[22px]',
  'text-emerald-800 hover:bg-emerald-50 dark:text-emerald-200 dark:hover:bg-emerald-900/40',
);

/** Super Helper / trailing control — end edge (left in RTL, right in LTR). */
export const mobileShellTopBarTrailingClassName = '-me-1.5 shrink-0';

type MobileShellTopBarProps = {
  menu: ReactNode;
  title: ReactNode;
  trailing?: ReactNode;
  className?: string;
};

/**
 * Compact mobile top bar shared by SaaS navbar and custom shell.
 * DOM order: menu | title | trailing (Super Helper). Document `dir` flips for HE vs EN.
 */
export function MobileShellTopBar({ menu, title, trailing, className }: MobileShellTopBarProps) {
  const { directionClass } = useRTLUtils();

  return (
    <header className={cn(mobileShellTopBarClassName, directionClass, className)}>
      {menu}
      {title}
      {trailing}
    </header>
  );
}

/** Super-admin mobile top bar: language globe beside Super Helper (not in side panel footer). */
export function MobileShellTopBarTrailing({ theme }: { theme: MobileSidePanelTheme }) {
  const { orgRole, sessionClaims, isLoaded, isSignedIn } = useAuth();
  const isSuperAdmin = isSuperAdminViewClient(orgRole, sessionClaims);

  return (
    <div className={cn('flex items-center gap-1.5 md:hidden', mobileShellTopBarTrailingClassName)}>
      {isLoaded && isSignedIn && isSuperAdmin ? (
        <MobileLanguageQuickButton theme={theme} />
      ) : null}
      <ReachCopilotTopbarButton embedded />
    </div>
  );
}
